// frontend/src/services/api.js - FIXED VERSION

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.testcrew.ai'

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  // ✅ ADDED: Helper method to get auth token
  getAuthToken() {
    return localStorage.getItem('token')
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    // Add auth token if available
    const token = this.getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        // Handle validation errors
        if (response.status === 422) {
          const errorData = await response.json()
          console.error('422 Validation Error:', errorData)
          throw new Error(`Validation Error: ${JSON.stringify(errorData)}`)
        }
        
        // Handle auth errors more gracefully
        if (response.status === 401) {
          // Only redirect to login if this is not a token verification request
          if (!endpoint.includes('/auth/me')) {
            localStorage.removeItem('token')
            window.location.href = '/login'
          }
          throw new Error('Authentication failed')
        }
        
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      
      return await response.text()
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  // Auth endpoints
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async getCurrentUser() {
    return this.request('/auth/me')
  }

  // Session endpoints
  async createSession(sessionData) {
    return this.request('/api/sessions/', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    })
  }

  async getSessions(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.request(`/api/sessions/${queryString ? `?${queryString}` : ''}`)
  }

  async getSession(id) {
    return this.request(`/api/sessions/${id}`)
  }

  async updateSession(id, data) {
    data.status=data.status.toLowerCase();
    return this.request(`/api/sessions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteSession(id) {
    return this.request(`/api/sessions/${id}`, {
      method: 'DELETE',
    })
  }

  // Test run endpoints
  async createTestRun(testRunData) {
    // ✅ ADDED: Debug logging
    console.log('Creating test run with data:', testRunData)
    return this.request('/api/automation/test-runs', {
      method: 'POST',
      body: JSON.stringify(testRunData),
    })
  }

  async getTestRuns(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.request(`/api/automation/test-runs${queryString ? `?${queryString}` : ''}`)
  }

  async getTestRun(id) {
    return this.request(`/api/automation/test-runs/${id}`)
  }

  // ✅ ADDED: Missing methods
  async updateTestRun(id, data) {
    return this.request(`/api/automation/test-runs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async createTestResult(testResultData) {
    return this.request('/api/automation/test-results', {
      method: 'POST',
      body: JSON.stringify(testResultData),
    })
  }

  // Conversation endpoints
  async createConversation(conversationData) {
    return this.request('/api/conversations', {
      method: 'POST',
      body: JSON.stringify(conversationData),
    })
  }

  async getConversations(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    return this.request(`/api/conversations${queryString ? `?${queryString}` : ''}`)
  }

  async addMessage(conversationId, messageData) {
    return this.request(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData),
    })
  }

  // ✅ FIXED: Screenshot creation with proper auth token handling
async createScreenshot(screenshotData) {
  try {
    console.log('📸 Creating screenshot with backend /upload endpoint:', {
      test_run_id: screenshotData.test_run_id,
      test_case_name: screenshotData.test_case_name,
      step_description: screenshotData.step_description,
      has_data: !!screenshotData.screenshot_data,
      data_length: screenshotData.screenshot_data?.length || 0
    })

    // Transform data to match your backend's expected format
    const backendData = {
      test_run_id: screenshotData.test_run_id,
      test_case_name: screenshotData.test_case_name,
      step_name: screenshotData.step_description, // Your backend expects "step_name"
      step_index: screenshotData.step_index || 0,
      image_data: screenshotData.screenshot_data, // Your backend expects "image_data"
      width: screenshotData.width,
      height: screenshotData.height,
      metadata: screenshotData.metadata || {}
    }

    const authToken = this.getAuthToken()
    
    const headers = {
      'Content-Type': 'application/json',
    }
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    // ✅ FIXED: Use correct endpoint path
    const response = await fetch(`${API_BASE_URL}/api/screenshots/upload`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(backendData)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('Screenshot upload failed:', errorText)
      throw new Error(`Failed to create screenshot: ${response.status} ${response.statusText}`)
    }
    
    return await response.json()
  } catch (error) {
    console.error('Error creating screenshot:', error)
    throw error
  }
}

  // ✅ ADD: Get screenshots for test run
  async getTestRunScreenshots(testRunId) {
    try {
      return await this.request(`/api/screenshots/test-run/${testRunId}`)
    } catch (error) {
      console.error('Error getting screenshots:', error)
      throw error
    }
  }

  async getTestCaseScreenshots(testRunId, testCaseName) {
    try {
      return await this.request(`/api/screenshots/test-case/${testRunId}/${encodeURIComponent(testCaseName)}`)
    } catch (error) {
      console.error('Error getting test case screenshots:', error)
      throw error
    }
  }
}

export default new ApiService()